import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = 'physiomobile_auth_token';
const USER_KEY = 'physiomobile_user';
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        
        // Verify token is still valid
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        }).catch(() => {
          // Token invalid, clear auth
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
        });
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inactivity timeout - auto logout after 30 minutes
  useEffect(() => {
    if (!user) return;

    const performLogout = () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
    };

    const checkInactivity = () => {
      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT) {
        performLogout();
      }
    };

    const interval = setInterval(checkInactivity, 60000); // Check every minute

    // Reset activity timer on user interactions
    const resetActivity = () => setLastActivity(Date.now());
    
    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);
    window.addEventListener('scroll', resetActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
      window.removeEventListener('scroll', resetActivity);
    };
  }, [user, lastActivity]);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    setToken(access_token);
    setUser(userData);
    setLastActivity(Date.now());
    
    return userData;
  }, []);

  const register = useCallback(async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      full_name: fullName 
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    setToken(access_token);
    setUser(userData);
    setLastActivity(Date.now());
    
    return userData;
  }, []);

  const requestMagicLink = useCallback(async (email) => {
    const response = await axios.post(`${API}/auth/magic-link/request`, { email });
    return response.data;
  }, []);

  const verifyMagicLink = useCallback(async (magicToken) => {
    const response = await axios.post(`${API}/auth/magic-link/verify`, { token: magicToken });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    setToken(access_token);
    setUser(userData);
    setLastActivity(Date.now());
    
    return userData;
  }, []);

  const checkInvite = useCallback(async (email) => {
    const response = await axios.post(`${API}/auth/check-invite`, { email });
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (e) {
      // Ignore logout errors
    }
    
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    requestMagicLink,
    verifyMagicLink,
    checkInvite,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
